import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const coreCards = [
  {
    icon: 'ri-file-list-3-line',
    title: '让一节课留下证据',
    desc: '从课堂切片、多模态理解和证据链回溯，为每一节课建立可回看、可验证的评价档案',
    color: 'primary',
  },
  {
    icon: 'ri-user-follow-line',
    title: '让一位教师看到成长',
    desc: '跨课程数据持续汇聚，形成教师个人成长轨迹，从单次反馈走向长期发展支持',
    color: 'accent',
  },
  {
    icon: 'ri-building-line',
    title: '让学校和区域看清方向',
    desc: '教研组统计、学校质量图、区域趋势图逐级汇总，让治理决策有数据可依',
    color: 'primary',
  },
];

const flowSteps = [
  { label: '单课评价', icon: 'ri-file-list-3-line' },
  { label: '四类对象服务', icon: 'ri-user-shared-line' },
  { label: '教师成长', icon: 'ri-user-follow-line' },
  { label: '教研改进', icon: 'ri-team-line' },
  { label: '分课程评价', icon: 'ri-stack-line' },
  { label: '学校质量', icon: 'ri-building-line' },
  { label: '区域治理', icon: 'ri-map-2-line' },
  { label: '跨学科迁移', icon: 'ri-global-line' },
];

export default function P82Closing({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.06);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.05);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-24 right-24 w-96 h-96 rounded-full bg-primary-100/10 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-20 left-16 w-[28rem] h-[28rem] rounded-full bg-accent-100/8 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-10 lg:px-16 pt-14 md:pt-16 pb-10 md:pb-14">
        {/* 标题区 */}
        <div ref={titleRef} className="text-center mb-8 md:mb-10">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />本部分小结
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            从评一节课到<span className="text-primary-600">提升一套质量体系</span>
          </h2>
          <p className={`text-base md:text-lg text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            AI评课从真实课堂证据出发，最终形成可持续、可追溯、可扩展的教学质量改进体系
          </p>
        </div>

        {/* 三列核心卡片 */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-10 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {coreCards.map((card, i) => (
            <div
              key={card.title}
              className={`p-6 md:p-8 rounded-2xl border transition-all duration-500 hover:scale-[1.02] hover:-translate-y-0.5 ${
                card.color === 'accent'
                  ? 'bg-accent-50/50 border-accent-200/40 hover:border-accent-300/60'
                  : 'bg-primary-50/50 border-primary-200/40 hover:border-primary-300/60'
              }`}
              style={{ transitionDelay: `${300 + i * 120}ms` }}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                card.color === 'accent'
                  ? 'bg-accent-100 border border-accent-200/50'
                  : 'bg-primary-100 border border-primary-200/50'
              }`}>
                <i className={`${card.icon} text-xl ${card.color === 'accent' ? 'text-accent-600' : 'text-primary-600'}`} />
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground-800 mb-2">{card.title}</h3>
              <p className="text-sm text-foreground-500 font-body leading-relaxed">{card.desc}</p>
            </div>
          ))}
        </div>

        {/* 完整链路 — 两行排布 */}
        <div className={`max-w-4xl mx-auto mb-8 md:mb-10 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <p className="text-center text-xs font-body font-semibold text-foreground-400 uppercase tracking-wider mb-4">完整链路</p>
          <div className="grid grid-cols-4 gap-3 md:gap-4">
            {flowSteps.map((step, i) => (
              <div
                key={step.label}
                className="flex flex-col items-center p-3 md:p-4 rounded-xl bg-background-100 border border-background-200/50 transition-all duration-300 hover:scale-105"
                style={{ transitionDelay: `${500 + i * 60}ms` }}
              >
                <div className="w-8 h-8 rounded-lg bg-background-50 border border-background-200/50 flex items-center justify-center mb-2">
                  <i className={`${step.icon} text-sm text-primary-600`} />
                </div>
                <span className="text-xs md:text-sm font-body font-bold text-foreground-700 text-center">{step.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 底部总结 */}
        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-5 md:p-6 rounded-2xl bg-accent-50/50 border border-accent-200/40">
            <p className="text-base md:text-lg text-foreground-700 font-body leading-relaxed">
              到这里，我们已经完成了从"AI能不能评"，到"AI怎样评"，再到<strong className="text-accent-700">"评完以后怎样用、怎样形成质量治理、怎样向其他课程迁移"</strong>的完整链路。
            </p>
          </div>
          <p className="text-sm text-foreground-400 font-body mt-4 italic">
            共同技术能力可以迁移，课程与学科专业判断必须分别建设
          </p>
        </div>
      </div>
    </div>
  );
}